import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Search, SlidersHorizontal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import CatalogFilters from "@/components/CatalogFilters";
import CatalogResults from "@/components/CatalogResults";
import CatalogPagination from "@/components/CatalogPagination";
import { useLanguage } from "@/context/LanguageContext";

import { API } from "@/lib/apiBase";

export default function Catalog() {
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();

  const urlSortBy = searchParams.get("sort") || "name";
  const urlSortOrder = searchParams.get("order") || "asc";
  const scoreMode = urlSortBy === "rating" && urlSortOrder === "desc";

  const [wines, setWines] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({ regions: [], grapes: [], years: [] });
  const [yearBounds, setYearBounds] = useState(null);
  const [priceBounds, setPriceBounds] = useState(null);

  const [search, setSearch] = useState("");
  const [region, setRegion] = useState("");
  const [grape, setGrape] = useState("");
  const [sortBy, setSortBy] = useState(urlSortBy);
  const [sortOrder, setSortOrder] = useState(urlSortOrder);

  // Keep sort state in sync with URL params (e.g. switching between nav-score and nav-catalog)
  useEffect(() => {
    setSortBy(urlSortBy);
    setSortOrder(urlSortOrder);
    setPage(1);
  }, [urlSortBy, urlSortOrder]);
  const [yearRange, setYearRange] = useState(null);
  const [priceRange, setPriceRange] = useState(null);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Load filter metadata once
  useEffect(() => {
    axios
      .get(`${API}/wines/filters`)
      .then((r) => {
        setFilters(r.data);
        if (r.data.year_range) {
          setYearBounds(r.data.year_range);
          setYearRange([r.data.year_range.min, r.data.year_range.max]);
        }
        if (r.data.price_range) {
          const bounds = {
            min: Math.floor(r.data.price_range.min),
            max: Math.ceil(r.data.price_range.max),
          };
          setPriceBounds(bounds);
          setPriceRange([bounds.min, bounds.max]);
        }
      })
      .catch((err) => console.error("Failed to load filters", err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill search from ?grape= query param
  useEffect(() => {
    const g = searchParams.get("grape");
    if (g) setSearch(g);
  }, [searchParams]);

  const yearActive = useMemo(
    () =>
      yearRange &&
      yearBounds &&
      (yearRange[0] !== yearBounds.min || yearRange[1] !== yearBounds.max),
    [yearRange, yearBounds]
  );
  const priceActive = useMemo(
    () =>
      priceRange &&
      priceBounds &&
      (priceRange[0] !== priceBounds.min || priceRange[1] !== priceBounds.max),
    [priceRange, priceBounds]
  );

  const fetchWines = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 12, sort_by: sortBy, sort_order: sortOrder };
      if (search) params.search = search;
      if (region) params.region = region;
      if (grape) params.grape = grape;
      if (yearActive) {
        params.min_year = yearRange[0];
        params.max_year = yearRange[1];
      }
      if (priceActive) {
        params.min_price = priceRange[0];
        params.max_price = priceRange[1];
      }
      const res = await axios.get(`${API}/wines`, { params });
      setWines(res.data.wines);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error("Failed to load wines", err);
      toast.error("Unable to load wines. Please try again.");
      setWines([]);
      setTotal(0);
      setPages(1);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, region, grape, sortBy, sortOrder, page, yearActive, priceActive, yearRange, priceRange]);

  useEffect(() => {
    fetchWines();
  }, [fetchWines]);

  const clearFilters = () => {
    setSearch("");
    setRegion("");
    setGrape("");
    setPage(1);
    if (yearBounds) setYearRange([yearBounds.min, yearBounds.max]);
    if (priceBounds) setPriceRange([priceBounds.min, priceBounds.max]);
  };

  const hasFilters = Boolean(search || region || grape || yearActive || priceActive);

  return (
    <main className="pt-24 pb-20 min-h-screen" data-testid="catalog-page">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="mb-12">
          <p className="font-accent text-xs tracking-[0.3em] text-gold/60 mb-2">
            {scoreMode ? "ALFREDWINE SCORE" : t.collectionLabel}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl text-white/90">
            {scoreMode ? "Ranked by Score" : t.wineCatalog}
          </h1>
          <p className="font-body text-sm text-white/40 mt-2">
            {scoreMode
              ? `${total} wines, highest-rated first`
              : `${total} ${t.winesAvailable}`}
          </p>
        </div>

        <div className="mb-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                data-testid="catalog-search"
                placeholder={t.searchPlaceholder}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-10 bg-wine-paper border-white/10 text-white/90 font-body text-sm placeholder:text-white/25 focus:border-merlot/50 h-11 rounded-sm"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters((v) => !v)}
              data-testid="catalog-filter-toggle"
              className="border-white/10 text-white/60 hover:text-white hover:bg-white/5 h-11 rounded-sm font-body text-sm gap-2"
            >
              <SlidersHorizontal className="w-4 h-4" /> {t.filters}
            </Button>
          </div>

          {showFilters && (
            <CatalogFilters
              filters={filters}
              region={region}
              grape={grape}
              sortBy={sortBy}
              sortOrder={sortOrder}
              yearRange={yearRange}
              priceRange={priceRange}
              yearBounds={yearBounds}
              priceBounds={priceBounds}
              hasFilters={hasFilters}
              onRegionChange={(v) => { setRegion(v === "all" ? "" : v); setPage(1); }}
              onGrapeChange={(v) => { setGrape(v === "all" ? "" : v); setPage(1); }}
              onSortChange={(v) => {
                const [s, o] = v.split("-");
                setSortBy(s);
                setSortOrder(o);
                setPage(1);
              }}
              onYearRangeChange={(v) => { setYearRange(v); setPage(1); }}
              onPriceRangeChange={(v) => { setPriceRange(v); setPage(1); }}
              onClear={clearFilters}
            />
          )}

          {hasFilters && (
            <div className="flex flex-wrap gap-2">
              {search && <Badge variant="outline" className="text-xs font-body text-white/60 border-white/15">Search: {search}</Badge>}
              {region && <Badge variant="outline" className="text-xs font-body text-white/60 border-white/15">{region}</Badge>}
              {grape && <Badge variant="outline" className="text-xs font-body text-white/60 border-white/15">{grape}</Badge>}
              {yearActive && <Badge variant="outline" className="text-xs font-body text-white/60 border-white/15">Year {yearRange[0]}–{yearRange[1]}</Badge>}
              {priceActive && <Badge variant="outline" className="text-xs font-body text-white/60 border-white/15">Price ${priceRange[0]}–${priceRange[1]}</Badge>}
            </div>
          )}
        </div>

        <CatalogResults wines={wines} loading={loading} />

        <CatalogPagination page={page} pages={pages} onChange={setPage} />
      </div>
    </main>
  );
}
